import apiService from './apiService';

const API_BASE = '/accounting';

export interface DeferredRevenue {
  id: string;
  name: string;
  referenceType: 'INVOICE' | 'MANUAL_ENTRY';
  referenceId?: string;
  deferredAccountId: string;
  revenueAccountId: string;
  totalAmount: number;
  recognizedAmount: number;
  remainingAmount: number;
  startDate: string;
  endDate: string;
  recognitionMethod: 'STRAIGHT_LINE' | 'CUSTOM';
  frequency: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL';
  state: 'DRAFT' | 'RUNNING' | 'FULLY_RECOGNIZED' | 'CANCELLED';
  recognitionState: 'PENDING' | 'PARTIAL' | 'COMPLETE';
  scheduleLines?: DeferredRevenueScheduleLine[];
  deferredAccount?: any;
  revenueAccount?: any;
  invoice?: any;
  initialJournalEntry?: any;
  createdByUser?: any;
  createdAt: string;
  updatedAt: string;
}

export interface DeferredRevenueScheduleLine {
  id: string;
  deferredRevenueId: string;
  periodStartDate: string;
  periodEndDate: string;
  amount: number;
  state: 'DRAFT' | 'POSTED';
  journalEntryId?: string;
  journalEntry?: any;
  postedAt?: string;
  postedBy?: string;
  postedByUser?: any;
}

export interface CreateDeferredRevenueData {
  name: string;
  referenceType: 'INVOICE' | 'MANUAL_ENTRY';
  referenceId?: string;
  deferredAccountId: string;
  revenueAccountId: string;
  totalAmount: number;
  startDate: string;
  endDate: string;
  recognitionMethod?: 'STRAIGHT_LINE' | 'CUSTOM';
  frequency?: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL';
}

export interface DeferredRevenueFilters {
  state?: 'DRAFT' | 'RUNNING' | 'FULLY_RECOGNIZED' | 'CANCELLED';
  recognitionState?: 'PENDING' | 'PARTIAL' | 'COMPLETE';
  startDateFrom?: string;
  startDateTo?: string;
  referenceType?: 'INVOICE' | 'MANUAL_ENTRY';
}

const deferredRevenueService = {
  /**
   * Get all deferred revenues with optional filters
   */
  getAll: async (filters?: DeferredRevenueFilters) => {
    const response = await apiService.get(`${API_BASE}/deferred-revenues`, { params: filters });
    return response.data;
  },

  /**
   * Get deferred revenue by ID
   */
  getById: async (id: string) => {
    const response = await apiService.get(`${API_BASE}/deferred-revenues/${id}`);
    return response.data;
  },

  /**
   * Create new deferred revenue
   */
  create: async (data: CreateDeferredRevenueData) => {
    const response = await apiService.post(`${API_BASE}/deferred-revenues`, data);
    return response.data;
  },

  /**
   * Update deferred revenue
   */
  update: async (id: string, data: Partial<CreateDeferredRevenueData>) => {
    const response = await apiService.put(`${API_BASE}/deferred-revenues/${id}`, data);
    return response.data;
  },

  /**
   * Delete deferred revenue
   */
  delete: async (id: string) => {
    const response = await apiService.delete(`${API_BASE}/deferred-revenues/${id}`);
    return response.data;
  },

  /**
   * Start recognition (DRAFT → RUNNING)
   */
  startRecognition: async (id: string, journalId: string) => {
    const response = await apiService.post(`${API_BASE}/deferred-revenues/${id}/start`, { journalId });
    return response.data;
  },

  /**
   * Post single schedule line
   */
  postScheduleLine: async (lineId: string, data: { journalId: string; accountingDate?: string }) => {
    const response = await apiService.post(`${API_BASE}/deferred-revenues/schedule-lines/${lineId}/post`, data);
    return response.data;
  },

  /**
   * Batch post multiple schedule lines
   */
  batchPostScheduleLines: async (
    id: string,
    data: { scheduleLineIds: string[]; journalId: string; accountingDate?: string }
  ) => {
    const response = await apiService.post(`${API_BASE}/deferred-revenues/${id}/schedule-lines/batch-post`, data);
    return response.data;
  },

  /**
   * Cancel deferred revenue
   */
  cancel: async (id: string, journalId: string, reason: string) => {
    const response = await apiService.post(`${API_BASE}/deferred-revenues/${id}/cancel`, { journalId, reason });
    return response.data;
  },

  /**
   * Get schedule lines for a deferred revenue
   */
  getScheduleLines: async (id: string) => {
    const response = await apiService.get(`${API_BASE}/deferred-revenues/${id}/schedule-lines`);
    return response.data;
  },
};

export default deferredRevenueService;
